// The `analysis` interface: the engine's scan of a whole game, stored once
// in the game's `analysis/` folder and read by every feature that grades a
// move: the arrows on the board, the shortlist, in time the verdict, the
// lessons, the puzzles and the mistake profile. A provider runs the scan
// over the `engine`; nothing here knows how the engine works or how a
// position is drawn.
import type { Judgement, MoveJudgement, PositionAnalysis, Scan, Score, San, Uci } from '@chess/api-model';

export const ANALYSIS_API = 'analysis';
export const ANALYSIS_VERSION = '1.0.0';

/** The file a scan is kept in, inside the game's `analysis/` folder. */
export const SCAN_FILE = 'scan.json';

/** v1's thresholds: a pawn or more is a blunder, a tenth of a pawn an inaccuracy. */
export const BLUNDER_CP = 100;
export const MISTAKE_CP = 50;
export const INACCURACY_CP = 10;
/** How many lines a scan keeps per position. */
export const LINES = 3;
/** How many plies of each line a scan keeps. */
export const PV_LENGTH = 8;

export type AnalysisEvent =
  | { type: 'progress'; gameId: string; done: number; total: number }
  | { type: 'complete'; gameId: string; scan: Scan }
  | { type: 'failed'; gameId: string; error: string };

export interface ScanStatus {
  running: boolean;
  done: number;
  total: number;
}

export interface Analysis {
  /** The stored scan, complete or not, or undefined if none has started. */
  get(gameId: string): Promise<Scan | undefined>;
  /**
   * The complete scan of a game: the stored one if complete, otherwise the
   * scan is run or continued, and the result stored. One scan runs at a time;
   * calls for the same game share it.
   */
  scan(gameId: string): Promise<Scan>;
  /** Where a running scan is, or undefined if none runs for the game. */
  status(gameId: string): ScanStatus | undefined;
  /** Hears progress, completion and failure. Returns the unsubscribe. */
  subscribe(listener: (event: AnalysisEvent) => void): () => void;
  /**
   * Pauses the scan while an interactive search needs the engine; the scan
   * resumes when every hold is released. Returns the release.
   */
  hold(): () => void;
}

/** A score as centipawns for the side to move; mates count as nearly won or lost. */
export function centipawns(score: Score): number {
  if (score.mate !== undefined) {
    if (score.mate === 0) return -10000; // the side to move is mated
    return score.mate > 0 ? 10000 - score.mate : -10000 - score.mate;
  }
  return score.cp ?? 0;
}

/** The same score seen from the other side. */
export function negate(score: Score): Score {
  if (score.mate !== undefined) return { mate: score.mate === 0 ? 0 : -score.mate };
  return { cp: -(score.cp ?? 0) };
}

/** A position's score from White's side, for a graph of the game. */
export function scoreForWhite(position: PositionAnalysis): Score {
  return position.fen.split(' ')[1] === 'b' ? negate(position.score) : position.score;
}

/** Grades a loss in centipawns; a move the engine would have played is always `best`. */
export function judge(delta: number, isBest: boolean): Judgement {
  if (isBest || delta <= 0) return 'best';
  if (delta >= BLUNDER_CP) return 'blunder';
  if (delta >= MISTAKE_CP) return 'mistake';
  if (delta >= INACCURACY_CP) return 'inaccuracy';
  return 'good';
}

/**
 * Judges the move from `before` to `after`: the mover's best line against
 * what the mover was left with after the reply, which `after` scores for
 * the opponent. Undefined when the engine had no line from `before`.
 */
export function judgeMove(
  before: PositionAnalysis,
  after: PositionAnalysis,
  move: { uci: Uci; san: San },
  ply: number,
): MoveJudgement | undefined {
  const best = before.lines[0];
  if (!best) return undefined;
  const isBest = best.uci === move.uci;
  const loss = centipawns(best.score) - centipawns(negate(after.score));
  const delta = isBest ? 0 : Math.max(0, loss);
  return { ply, uci: move.uci, san: move.san, best: best.uci, delta, judgement: judge(delta, isBest) };
}

/** The scan's entry for a ply, if it has one. */
export function positionAt(scan: Scan | undefined, ply: number): PositionAnalysis | undefined {
  return scan?.positions.find((p) => p.ply === ply);
}

/** The judgement of the move played from a ply, if the scan has judged it. */
export function judgementFrom(scan: Scan | undefined, ply: number): MoveJudgement | undefined {
  return scan?.moves.find((m) => m.ply === ply + 1);
}

/** Reads a stored scan, or throws when it is not one this version understands. */
export function parseScan(text: string): Scan {
  const value = JSON.parse(text) as Partial<Scan>;
  if (value.schemaVersion !== 1 || typeof value.gameId !== 'string' || !Array.isArray(value.positions)) {
    throw new Error('not a scan this version understands');
  }
  return {
    schemaVersion: 1,
    gameId: value.gameId,
    engine: value.engine ?? '',
    depth: value.depth ?? 0,
    total: value.total ?? 0,
    positions: value.positions,
    moves: Array.isArray(value.moves) ? value.moves : [],
    complete: value.complete === true,
    updatedAt: value.updatedAt ?? '',
  };
}

export { createAnalysis } from './scan.ts';
export type { AnalysisDeps } from './scan.ts';
